import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import useSimplePracticeInvites from '../hooks/Clinic/useSimplePracticeInvites';
import { fetchCliniciansByPracticeId } from '../redux/clinic/clinicSlice';
import { useIntegrationsByPracticeId } from '../hooks/Integration/useIntegration';
import { useLatestScraperRun } from '../hooks/Scraper/useLatestScraperRun';
import { useCurrentSubscriptionCost } from '../hooks/Billing/useCurrentSubscriptionCost';
import { isClinicianMember } from '../utils/scraperRun';
import { ScrapedTeamMember } from '../redux/scraper/scraperSlice';
import { AppDispatch } from '../redux';
import { motion, AnimatePresence } from 'framer-motion';
import { DashboardLayout } from '../layouts/DashboardLayout';
import { SendInviteModal } from '../components/modals/SendInviteModal';
import { InvitationBillingData } from '../hooks/useStripeInvitation';
import { useStripeProducts } from '../hooks/Stripe/useStripeProducts';
import { io, Socket } from 'socket.io-client';
import { toast } from 'react-toastify';
import {
  CheckSquare,
  Bell,
  Mail,
  Eye,
  CheckCircle,
  RefreshCw,
  Send,
  CheckCircle2,
  Tag,
  AlertCircle
} from 'lucide-react';

// ============================================================================
// UTILITY FUNCTIONS (Extracted outside component for better performance)
// ============================================================================

const getInitials = (firstName: string, lastName: string) => {
  const first = firstName?.[0] || 'D';
  const last = lastName?.[0] || 'r';
  return `${first}${last}`.toUpperCase();
};

const formatDate = (dateString: string | number | undefined | null): string => {
  if (!dateString || dateString === '—') return '—';
  try {
    let date: Date;
    // Handle timestamp (number) - could be in seconds or milliseconds
    if (typeof dateString === 'number') {
      // If timestamp is in seconds (less than year 2000 in milliseconds), convert to milliseconds
      if (dateString < 946684800000) {
        date = new Date(dateString * 1000);
      } else {
        date = new Date(dateString);
      }
    } else {
      date = new Date(dateString);
    }
    if (isNaN(date.getTime())) return '—';
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  } catch {
    return '—';
  }
};

const getClinicianCompletion = (clinician: any) => {
  const profile = clinician?.profile || {};
  const required = [
    { name: 'Specialties', value: Array.isArray(clinician?.specialties) && clinician.specialties.length > 0 },
    { name: 'Rates', value: Array.isArray(profile?.rates) && profile.rates.length > 0 },
    { name: 'Modalities', value: Array.isArray(profile?.modalities) && profile.modalities.length > 0 },
    { name: 'Age Ranges', value: Array.isArray(profile?.ageRanges) && profile.ageRanges.length > 0 },
    { name: 'Session Types', value: Array.isArray(profile?.sessionTypes) && profile.sessionTypes.length > 0 },
    { name: 'Insurance Accepted', value: Array.isArray(profile?.insuranceAccepted) && profile.insuranceAccepted.length > 0 },
    { name: 'Languages', value: Array.isArray(profile?.languages) && profile.languages.length > 0 },
  ];
  const missing = required.filter(r => !r.value).map(r => r.name);
  const completedCount = required.length - missing.length;
  const percent = Math.round((completedCount / required.length) * 100);
  return { percent, missing };
};

const getInviteStatus = (clinician: any): 'new' | 'invited' | 'clicked' | 'completed' | 'expired' | null => {
  // Check if clinician has a status field first
  const clinicianStatus = clinician?.status?.toLowerCase();
  if (clinicianStatus === 'new' || clinicianStatus === 'invited' ||
    clinicianStatus === 'clicked' || clinicianStatus === 'completed' || clinicianStatus === 'expired') {
    return clinicianStatus as 'new' | 'invited' | 'clicked' | 'completed' | 'expired';
  }

  // Check invite object
  const invite = clinician?.invite;
  if (!invite) {
    // If no invite object, check if they're in the invited list
    // Default to 'invited' if they're in the invited clinicians list
    return 'invited';
  }

  // Check if invite is expired
  if (invite.date_expiration) {
    const expirationDate = new Date(invite.date_expiration);
    if (new Date() > expirationDate) {
      return 'expired';
    }
  }

  // Check if invite is completed
  if (invite.already_used === true) return 'completed';

  // Check if invite was clicked
  if (invite.date_clicked || invite.clicked_at) {
    return 'clicked';
  }

  // Check if invite was sent
  // if (invite.date_sent) {
  //   return 'sent';
  // }

  // Default to new if invite exists but no date_sent
  return 'new';
};

/** Workforce: clinicians with status "new" always appear until invited (skip calendar-scrape / active gates). */
const isClinicianNewForWorkforce = (clinician: any): boolean => {
  const s = String(clinician?.status ?? '').toLowerCase();
  if (s === 'new') return true;
  return getInviteStatus(clinician) === 'new';
};

const getStatusBadge = (status: 'new' | 'invited' | 'clicked' | 'completed' | 'expired' | null) => {
  switch (status) {
    case 'completed':
      return {
        text: 'completed',
        className: 'bg-green-100 text-green-700',
        icon: CheckCircle2
      };
    case 'clicked':
      return {
        text: 'clicked',
        className: 'bg-blue-100 text-blue-700',
        icon: Eye
      };
    // case 'sent':
    //   return {
    //     text: 'sent',
    //     className: 'bg-indigo-100 text-indigo-700',
    //     icon: Send
    //   };
    case 'invited':
      return {
        text: 'invited',
        className: 'bg-green-100 text-green-700',
        icon: CheckSquare
      };
    case 'expired':
      return {
        text: 'expired',
        className: 'bg-red-100 text-red-700',
        icon: AlertCircle
      };
    case 'new':
      return {
        text: 'new',
        className: 'bg-gray-100 text-gray-700',
        icon: Bell
      };
    default:
      return {
        text: 'invited',
        className: 'bg-green-100 text-green-700',
        icon: CheckSquare
      };
  }
};

// Animation variants
const listVariants = {
  hidden: { opacity: 0 },
  show: {
    opacity: 1,
    transition: {
      staggerChildren: 0.06,
    },
  },
};

const cardVariants = {
  hidden: { opacity: 0, y: 40, scale: 0.9 },
  show: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: {
      type: "spring",
      stiffness: 100,
      damping: 25
    }
  },
};

const rowVariants = {
  hidden: { opacity: 0, y: 20, scale: 0.95 },
  show: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: {
      type: "spring",
      stiffness: 120,
      damping: 20
    }
  },
};

const WORKFORCE_COST_PER_CLINICIAN = 59;

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export default function InvitationManagement() {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();

  // ========== STATE ==========
  const [isSendInviteModalOpen, setIsSendInviteModalOpen] = useState(false);
  const [pendingInviteData, setPendingInviteData] = useState<InvitationBillingData | null>(null);
  const [invitedIds, setInvitedIds] = useState<Record<string, boolean>>({});
  const [selectedIds, setSelectedIds] = useState<Record<string, boolean>>({});

  // ========== REDUX STATE ==========
  const practiceCliniciansRaw = useSelector((state: any) => state?.clinics?.practiceClinicians);
  const practiceClinicians = Array.isArray(practiceCliniciansRaw) ? practiceCliniciansRaw : [];
  const isLoading = useSelector((state: any) => state?.clinics?.isLoading || false);
  const selectedPractice = useSelector((state: any) => state?.practices?.selectedPractice);

  // ========== HOOKS ==========
  const { inviteOne, inviteBulk, loading: inviteLoading } = useSimplePracticeInvites();
  const importSocketRef = useRef<Socket | null>(null);

  // ========== MEMOIZED VALUES ==========
  // Filter candidates - only compute when dependencies change
  const simplePracticeCandidates = useMemo(() => {
    return practiceClinicians.filter((c: any) => {
      const hasLastCalendarScrape = Boolean(c?.lastCalendarScrape || c?.profile?.detailsScrapedAt);
      const alreadyInvited = Boolean(c?.invite || invitedIds[c?._id] || invitedIds[c?.id]);
      const isActive = c?.isActive === true; // Only show if isActive is false or undefined
      if (alreadyInvited) return false;
      if (isClinicianNewForWorkforce(c)) return true;
      return hasLastCalendarScrape && !isActive;
    });
  }, [practiceClinicians, invitedIds]);

  const invitedClinicians = useMemo(() => {
    return practiceClinicians.filter((c: any) => {
      const hasLastCalendarScrape = Boolean(c?.lastCalendarScrape || c?.profile?.detailsScrapedAt);
      const alreadyInvited = Boolean(c?.invite || invitedIds[c?._id] || invitedIds[c?.id]);
      const isActive = c?.isActive === true; // Only show if isActive is false or undefined (can still pay)
      return hasLastCalendarScrape && alreadyInvited && !isActive;
    });
  }, [practiceClinicians, invitedIds]);

  // Get practiceId from localStorage (same method as DashboardLayout)
  // localStorage 'user' object structure:
  // {
  //   sub: "110b25c0-2021-70a7-708a-dbaa643879ad",
  //   email_verified: true,
  //   "custom:id_practice": "6929c87522b42cd6c17cae2d",
  //   "custom:user_role": "practice_admin",
  //   email: "info+one@erevu.works",
  //   given_name: "Test",
  //   family_name: "One",
  //   ...other Cognito token fields
  // }
  const practiceId = useMemo(() => {
    try {
      // First try to get from localStorage (same as DashboardLayout)
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      const localStoragePracticeId = user?.["custom:id_practice"];

      if (localStoragePracticeId) {
        return localStoragePracticeId;
      }

      // Fallback to selectedPractice or clinician data
      const fallbackPracticeId = selectedPractice?._id
        || selectedPractice?.id
        || simplePracticeCandidates[0]?.practice_id
        || null;

      if (fallbackPracticeId) {
      } else {
        console.warn('❌ Practice ID not found in localStorage or fallback sources:', {
          localStorageUser: user,
          selectedPractice: selectedPractice?._id || selectedPractice?.id,
          firstClinicianPracticeId: simplePracticeCandidates[0]?.practice_id,
        });
      }

      return fallbackPracticeId;
    } catch (error) {
      console.error('❌ Error parsing practiceId from localStorage:', error);
      // Fallback to selectedPractice or clinician data
      return selectedPractice?._id
        || selectedPractice?.id
        || simplePracticeCandidates[0]?.practice_id
        || null;
    }
  }, [selectedPractice, simplePracticeCandidates]);

  const { integration } = useIntegrationsByPracticeId(practiceId || '');
  const hasConfiguredIntegration = Boolean((integration as { id?: string | null } | null)?.id);
  const isIntegrationConnected = ['active', 'connected'].includes(
    String((integration as { connectionStatus?: string } | null)?.connectionStatus || '').toLowerCase()
  );

  // Latest scraper run: the source of importable team members
  const { run: latestScraperRun, loading: scraperRunLoading } = useLatestScraperRun(practiceId || '');

  // All Clinician team members from the latest import (is_clinician === true)
  const clinicianMembers = useMemo(() => {
    const members: ScrapedTeamMember[] = latestScraperRun?.scraperResult || [];
    return members.filter(isClinicianMember);
  }, [latestScraperRun]);

  // Emails of clinicians already in this practice (GET /practice/{id}/clinicians)
  const existingEmails = useMemo(() => new Set(
    practiceClinicians
      .map((c: any) => (c?.email || '').toLowerCase())
      .filter(Boolean)
  ), [practiceClinicians]);

  const isAlreadyAdded = useCallback(
    (m: ScrapedTeamMember) => !!m.email && existingEmails.has(m.email.toLowerCase()),
    [existingEmails]
  );

  // Clinicians not yet added to the practice — the only selectable rows
  const newMembers = useMemo(
    () => clinicianMembers.filter(m => !isAlreadyAdded(m)),
    [clinicianMembers, isAlreadyAdded]
  );

  const selectedMembers = useMemo(
    () => newMembers.filter(m => selectedIds[m.email.toLowerCase()]),
    [newMembers, selectedIds]
  );

  const allSelected = newMembers.length > 0 && selectedMembers.length === newMembers.length;

  const toggleSelectAll = useCallback(() => {
    setSelectedIds(prev => {
      const everySelected = newMembers.length > 0 && newMembers.every(m => prev[m.email.toLowerCase()]);
      if (everySelected) return {};
      const next: Record<string, boolean> = {};
      newMembers.forEach(m => { next[m.email.toLowerCase()] = true; });
      return next;
    });
  }, [newMembers]);

  const userId = useMemo(() => {
    return JSON.parse(localStorage.getItem('user') || '{}')?.["sub"];
  }, []);

  // Fetch Stripe products to get membership price
  const { products: stripeProducts, isLoading: isLoadingProducts } = useStripeProducts({
    autoFetch: true,
    activeOnly: true,
  });

  // Get membership product and price
  const membershipPriceInfo = useMemo(() => {

    if (!stripeProducts || stripeProducts.length === 0) {
      console.warn('⚠️ No Stripe products available yet');
      return null;
    }

    // Find the "Membership" product
    const membershipProduct = stripeProducts.find(p =>
      p.name?.toLowerCase() === 'membership'
    );

    if (!membershipProduct) {
      console.warn('⚠️ Membership product not found in Stripe products', {
        availableProducts: stripeProducts.map(p => p.name),
      });
      return null;
    }


    // Get the price - check defaultPrice/default_price first, then prices array
    // The API response structure: { defaultPrice: { id, currency, unitAmount, recurring, type }, prices: [...] }
    // Note: API uses camelCase (defaultPrice) but TypeScript type uses snake_case (default_price)
    let price = null;
    const productAny = membershipProduct as any;
    const defaultPrice = productAny.defaultPrice || membershipProduct.default_price;

    // Check if defaultPrice is an object with price details
    if (defaultPrice) {
      if (typeof defaultPrice === 'object') {
        // Check if it has unitAmount or unit_amount
        const defaultPriceObj = defaultPrice;
        if (defaultPriceObj.unitAmount !== undefined || defaultPriceObj.unit_amount !== undefined) {
          price = defaultPriceObj;
        } else if (membershipProduct.prices && membershipProduct.prices.length > 0) {
          // If defaultPrice doesn't have amount, try to find it in prices array by ID
          const defaultPriceId = defaultPriceObj.id || defaultPrice;
          price = membershipProduct.prices.find(p => p.id === defaultPriceId);
        }
      } else if (typeof defaultPrice === 'string' && membershipProduct.prices) {
        // defaultPrice is just an ID string, find it in prices array
        price = membershipProduct.prices.find(p => p.id === defaultPrice);
      }
    }

    // Fallback to first active price if no defaultPrice found
    if (!price && membershipProduct.prices && membershipProduct.prices.length > 0) {
      price = membershipProduct.prices.find(p => p.active) || membershipProduct.prices[0];
    }

    if (!price) {
      console.warn('⚠️ No price found for Membership product');
      return null;
    }

    // Extract unit amount - handle both unitAmount (from API) and unit_amount (from Stripe SDK)
    const unitAmount = (price as any).unitAmount ?? (price as any).unit_amount ?? price.unit_amount;

    // Log the full price object for debugging

    // If unitAmount is null/undefined, return null (no fallback)
    if (!unitAmount || unitAmount === null) {
      console.error('❌ Price unitAmount is null in API response', {
        priceId: price.id,
        priceType: price.type,
        recurring: price.recurring,
        fullPriceObject: price,
        note: 'Backend needs to fetch full price details using stripe.prices.retrieve() to get unit_amount. See BACKEND_UPDATE_STRIPE_PRODUCTS.md for the fix.'
      });
      return null;
    }

    // Return price in dollars (convert from cents)
    const priceInDollars = unitAmount > 0 ? unitAmount / 100 : null;


    return {
      price: priceInDollars,
      priceId: price.id,
      productId: membershipProduct.id,
    };
  }, [stripeProducts]);

  // Extract price, priceId, and productId for backward compatibility
  const membershipPrice = membershipPriceInfo?.price ?? null;
  const membershipPriceId = membershipPriceInfo?.priceId ?? null;
  const membershipProductId = membershipPriceInfo?.productId ?? null;

  // Prefer the live Stripe Membership price; WORKFORCE_COST_PER_CLINICIAN is only a fallback
  const effectivePerInviteCost = membershipPrice ?? WORKFORCE_COST_PER_CLINICIAN;

  // Practice's current subscription total, for the payment modal's "total after" line
  const { currentMonthlyCost } = useCurrentSubscriptionCost(practiceId || '', effectivePerInviteCost);

  // Calculate stats for Workforce page
  const stats = useMemo(() => {
    let invitedCount = 0;
    let expiredCount = 0;
    practiceClinicians.forEach((c: { status?: string; invite?: { date_expiration?: string; already_used?: boolean; date_clicked?: string; clicked_at?: string; date_sent?: string } }) => {
      const status = getInviteStatus(c);
      if (status === 'invited' || status === 'clicked' || status === 'completed') invitedCount += 1;
      if (status === 'expired') expiredCount += 1;
    });

    return {
      totalClinicians: practiceClinicians.length,
      totalInvited: invitedCount,
      totalExpired: expiredCount,
      availableForInvitation: newMembers.length,
      selectedForInvite: Object.keys(selectedIds).filter(id => selectedIds[id]).length,
    };
  }, [practiceClinicians, newMembers.length, selectedIds]);

  // ========== CALLBACKS ==========
  const markInvited = useCallback((id: string) => {
    setInvitedIds(prev => ({ ...prev, [id]: true }));
  }, []);

  const toggleSelected = useCallback((id: string) => {
    setSelectedIds(prev => ({ ...prev, [id]: !prev[id] }));
  }, []);


  const handleSendInvite = useCallback(async (c: any) => {
    // Check if products are still loading
    if (isLoadingProducts) {
      toast.info('Loading membership pricing... Please wait.');
      return;
    }

    // Require Stripe price/product IDs for backend payment intent creation
    if (!membershipPriceId || !membershipProductId) {
      console.error('❌ Membership priceId or productId is null. Debug info:', {
        stripeProducts: stripeProducts?.length || 0,
        isLoadingProducts,
        products: stripeProducts,
        membershipPrice,
        membershipPriceId,
        membershipProductId,
      });
      toast.error('Membership billing configuration is not available. Please try again.');
      return;
    }
    const baseAmount = effectivePerInviteCost;

    if (!membershipPriceId || !membershipProductId) {
      toast.error('Price ID or Product ID not available. Please try again.');
      return;
    }

    if (!c.practice_id) {
      toast.error('Practice ID not found for this clinician.');
      return;
    }

    // Get customerId from localStorage
    const getCustomerId = () => {
      try {
        const user = JSON.parse(localStorage.getItem('user') || '{}');
        return user?.stripe_customer_id || user?.customerId || undefined;
      } catch {
        return undefined;
      }
    };

    const billingData: InvitationBillingData = {
      inviteCount: 1,
      totalAmount: baseAmount, // Base amount, discount applied in modal
      clinicianNames: [c.fullName || `${c.firstName || ''} ${c.lastName || ''}`.trim()],
      clinicianIds: [c._id || c.id || ''], // Include MongoDB _id
      perInviteCost: effectivePerInviteCost,
      priceId: membershipPriceId,
      productId: membershipProductId,
      practiceId: c.practice_id,
      customerId: getCustomerId(),
    };

    setPendingInviteData(billingData);
    setIsSendInviteModalOpen(true);
  }, [membershipPrice, membershipPriceId, membershipProductId, isLoadingProducts, stripeProducts, effectivePerInviteCost]);

  const confirmSingleInvite = useCallback(async () => {
    if (!pendingInviteData) return;

    const clinicianToInvite = simplePracticeCandidates.find((c: any) =>
      (c.fullName || `${c.firstName || ''} ${c.lastName || ''}`.trim()) === pendingInviteData.clinicianNames[0]
    );

    if (clinicianToInvite) {
      const ok = await inviteOne(clinicianToInvite._id || clinicianToInvite.id, clinicianToInvite.email);
      if (ok) {
        markInvited(clinicianToInvite._id || clinicianToInvite.id);
        setIsSendInviteModalOpen(false);
        setPendingInviteData(null);
        toast.success('Invitation sent successfully!');
      }
    }
  }, [pendingInviteData, simplePracticeCandidates, inviteOne, markInvited]);

  const handleBulkInvite = useCallback(async () => {
    if (selectedMembers.length === 0) {
      toast.warning('Select clinicians to invite');
      return;
    }

    // Check if products are still loading
    if (isLoadingProducts) {
      toast.info('Loading membership pricing... Please wait.');
      return;
    }

    // Require Stripe price/product IDs for backend payment intent creation
    if (!membershipPriceId || !membershipProductId) {
      console.error('❌ Membership priceId or productId is null. Debug info:', {
        stripeProducts: stripeProducts?.length || 0,
        isLoadingProducts,
        products: stripeProducts,
        membershipPrice,
        membershipPriceId,
        membershipProductId,
      });
      toast.error('Membership billing configuration is not available. Please try again.');
      return;
    }

    if (!practiceId) {
      toast.error('Practice ID not found. Please ensure you are logged in correctly.');
      return;
    }

    const pricePerInvite = effectivePerInviteCost;
    const baseAmount = selectedMembers.length * pricePerInvite;

    // Get customerId from localStorage
    const getCustomerId = () => {
      try {
        const user = JSON.parse(localStorage.getItem('user') || '{}');
        return user?.stripe_customer_id || user?.customerId || undefined;
      } catch {
        return undefined;
      }
    };

    // Persist the selection: Stripe's redirect to the success page loses React
    // state, and PaymentSuccessPage reads this key to finish adding the clinicians.
    localStorage.setItem('pendingScrapedClinicians', JSON.stringify({
      practiceId,
      practiceName: selectedPractice?.name || '',
      members: selectedMembers
    }));

    const billingData: InvitationBillingData = {
      inviteCount: selectedMembers.length,
      totalAmount: baseAmount, // Base amount, discount applied in modal
      clinicianNames: selectedMembers.map(m => m.name),
      clinicianIds: selectedMembers.map(m => m.id),
      perInviteCost: pricePerInvite,
      priceId: membershipPriceId,
      productId: membershipProductId,
      practiceId,
      customerId: getCustomerId(),
    };

    setPendingInviteData(billingData);
    setIsSendInviteModalOpen(true);
  }, [selectedMembers, practiceId, membershipPrice, membershipPriceId, membershipProductId, isLoadingProducts, stripeProducts, effectivePerInviteCost]);

  // Note: purchases are finalized entirely by PaymentSuccessPage. Stripe always
  // redirects there after confirmPayment, so this page never sees a completed
  // payment — it just refetches clinicians on mount when the user navigates back.

  // ========== EFFECTS ==========
  // Fetch this practice's current clinicians on mount
  useEffect(() => {
    if (practiceId) {
      dispatch(fetchCliniciansByPracticeId({ practiceId }));
    }
  }, [dispatch, practiceId]);

  // Socket.IO setup (optimized with proper cleanup)
  useEffect(() => {
    const user = localStorage.getItem('user');
    if (!user) return;

    const userData = JSON.parse(user);
    const cognito_sub = userData?.["sub"];
    const id_practice = selectedPractice?._id || selectedPractice?.id || practiceId;

    if (!cognito_sub || !id_practice) return;

    const SOCKET_URL = import.meta.env.VITE_SOCKET_URL || 'https://socketio.therapistdirect.org';

    const socket = io(`${SOCKET_URL}/`, {
      query: {
        subscriber: cognito_sub,
        practiceId: id_practice,
        integrationType: 'simplepractice'
      },
      timeout: 20000,
      reconnection: true,
      reconnectionAttempts: 5,
      reconnectionDelay: 2000,
      transports: ['websocket', 'polling']
    });

    importSocketRef.current = socket;

    const refreshClinicians = () => dispatch(fetchCliniciansByPracticeId({ practiceId: id_practice }));

    const handleProgress = (data: any) => {
      if (data.status === 'in_progress' || data.status === 'in-progress') {
        refreshClinicians();

        if (typeof data.progress === 'number') {
          toast.info(`Importing... ${data.progress}%`, {
            position: "top-right",
            autoClose: 2000,
            toastId: 'import-progress',
          });
        }
      } else if (data.status === 'completed' || data.status === 'complete') {
        refreshClinicians();
        toast.success('Clinicians imported successfully!');
      }
    };

    const handleServerData = (data: any) => {
      if (data.success || data.completed || data.status === 'completed' || data.status === 'complete') {
        refreshClinicians();
        toast.success(data.message || 'Clinicians imported successfully!');
      }
    };

    const handleSuccess = (data: any) => {
      refreshClinicians();
      toast.success(data.message || 'Clinicians imported successfully!');
    };

    const handleClinicianImported = (data: any) => {
      refreshClinicians();
      const clinicianName = data.name || data.fullName || data.firstName || 'Clinician';
      toast.success(`New clinician added: ${clinicianName}`);
    };

    socket.on('progress', handleProgress);
    socket.on('server:data', handleServerData);
    socket.on('success', handleSuccess);
    socket.on('clinician:imported', handleClinicianImported);

    return () => {
      socket.off('progress', handleProgress);
      socket.off('server:data', handleServerData);
      socket.off('success', handleSuccess);
      socket.off('clinician:imported', handleClinicianImported);
      socket.disconnect();
      importSocketRef.current = null;
    };
  }, [dispatch, selectedPractice, practiceId]);

  // Handle Stripe Checkout return and Payment Intent redirect
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const setupStatus = urlParams.get('setup');
    const paymentStatus = urlParams.get('payment');
    const redirectStatus = urlParams.get('redirect_status');
    const paymentIntent = urlParams.get('payment_intent');

    // Handle payment method setup completion
    if (setupStatus) {
      window.history.replaceState({}, '', window.location.pathname);
      // ... existing logic ...
    }

    if (setupStatus === 'complete') {
      toast.success('Payment method added successfully!');
    }

    // Handle payment intent redirect (from 3D Secure or other authentication)
    if (paymentStatus === 'success' && redirectStatus === 'succeeded' && paymentIntent) {
      // Clean up URL parameters
      window.history.replaceState({}, '', window.location.pathname);

      // Show success message
      toast.success('Payment completed successfully!');


      // If there's pending invite data, you might want to process it here
      // or the payment processing should have already been handled by your backend
    } else if (paymentStatus === 'success' && redirectStatus === 'failed') {
      // Clean up URL parameters
      window.history.replaceState({}, '', window.location.pathname);

      toast.error('Payment failed. Please try again.');
    }
  }, []);

  // ========== RENDER ==========
  return (
    <DashboardLayout>
      <div className="min-h-screen bg-gray-50">
        <div className="flex-1 overflow-auto">
          <div className="p-6 md:p-8">
            {/* Header */}
            <motion.div
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              className="flex items-center justify-between mb-6 p-6 backdrop-blur-sm bg-white/60 rounded-3xl shadow-[8px_8px_16px_rgba(0,0,0,0.08),-8px_-8px_16px_rgba(255,255,255,0.8)] hover:shadow-[12px_12px_24px_rgba(0,0,0,0.1),-12px_-12px_24px_rgba(255,255,255,0.9)] transition-all duration-300 border border-white/50"
            >
              <div>
                <h1 className="text-2xl md:text-3xl font-semibold text-gray-800 mb-1">
                  Workforce Management
                </h1>
                <p className="text-sm text-gray-500">Manage clinician billing and invitations</p>
              </div>
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={handleBulkInvite}
                disabled={inviteLoading || newMembers.length === 0}
                className="flex items-center gap-2 bg-indigo-500 text-white px-5 py-2.5 rounded-xl font-medium shadow-[4px_4px_8px_rgba(99,102,241,0.3),-2px_-2px_4px_rgba(255,255,255,0.2)] hover:shadow-[6px_6px_12px_rgba(99,102,241,0.4),-3px_-3px_6px_rgba(255,255,255,0.3)] active:shadow-[inset_2px_2px_4px_rgba(79,70,229,0.3)] transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Send className="w-4 h-4" />
                {inviteLoading ? 'Processing...' : `Invite New Clinicians${selectedMembers.length > 0 ? ` (${selectedMembers.length})` : ''}`}
              </motion.button>
            </motion.div>


            {/* Workforce Page Stats Cards */}
            <motion.div
              variants={listVariants}
              initial="hidden"
              animate="show"
              className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6"
            >
              {[
                {
                  icon: Eye,
                  value: stats.totalClinicians,
                  label: 'Total Clinicians',
                  iconBg: 'bg-slate-100',
                  iconColor: 'text-slate-600',
                  description: 'In your practice'
                },
                {
                  icon: Mail,
                  value: stats.totalInvited,
                  label: 'Total Invited',
                  iconBg: 'bg-blue-100',
                  iconColor: 'text-blue-600',
                  description: 'Invitation sent at some point'
                },
                {
                  icon: AlertCircle,
                  value: stats.totalExpired,
                  label: 'Total Expired',
                  iconBg: 'bg-red-100',
                  iconColor: 'text-red-600',
                  description: 'Invitations expired'
                },
                {
                  icon: Bell,
                  value: stats.availableForInvitation,
                  label: 'Available for Invitation',
                  iconBg: 'bg-purple-100',
                  iconColor: 'text-purple-600',
                  description: 'Ready to invite'
                }
              ].map((stat, index) => {
                const Icon = stat.icon;
                return (
                  <motion.div
                    key={index}
                    variants={cardVariants}
                    whileHover={{ y: -2, scale: 1.01 }}
                    className="backdrop-blur-sm bg-white/50 rounded-2xl p-5 shadow-[6px_6px_12px_rgba(0,0,0,0.06),-6px_-6px_12px_rgba(255,255,255,0.7)] hover:shadow-[8px_8px_16px_rgba(0,0,0,0.08),-8px_-8px_16px_rgba(255,255,255,0.9)] active:shadow-[inset_3px_3px_6px_rgba(0,0,0,0.05),inset_-3px_-3px_6px_rgba(255,255,255,0.8)] transition-all duration-200 border border-white/40"
                  >
                    <div className="flex items-center justify-between mb-3">
                      <div className={`w-10 h-10 ${stat.iconBg} ${stat.iconColor} rounded-xl flex items-center justify-center shadow-[inset_2px_2px_4px_rgba(0,0,0,0.1),inset_-2px_-2px_4px_rgba(255,255,255,0.9)]`}>
                        <Icon className="w-5 h-5" />
                      </div>
                    </div>
                    <div className="text-2xl font-semibold text-gray-800 mb-1">{stat.value}</div>
                    <div className="text-sm text-gray-700 font-medium mb-1">{stat.label}</div>
                    <div className="text-xs text-gray-500">{stat.description}</div>
                  </motion.div>
                );
              })}
            </motion.div>

            {/* Workforce Management List */}
            <motion.div
              initial={{ opacity: 0, y: 40 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="backdrop-blur-sm bg-white/50 rounded-3xl shadow-[8px_8px_16px_rgba(0,0,0,0.06),-8px_-8px_16px_rgba(255,255,255,0.7)] overflow-hidden border border-white/40"
            >
              <div className="p-6">
                <div className="mb-4 text-sm text-gray-500">
                  Team members with a <span className="font-medium text-gray-700">Clinician</span> role from your latest SimplePractice import who have not been added to your practice yet.
                </div>
                {clinicianMembers.length > 0 && (
                  <div className="flex items-center gap-3 mb-4 px-1">
                    <input
                      type="checkbox"
                      checked={allSelected}
                      disabled={newMembers.length === 0}
                      ref={el => { if (el) el.indeterminate = selectedMembers.length > 0 && !allSelected; }}
                      onChange={toggleSelectAll}
                      className="w-4 h-4 text-indigo-600 rounded border-gray-300 focus:ring-indigo-500 cursor-pointer disabled:cursor-not-allowed"
                    />
                    <span className="text-sm font-medium text-gray-700">
                      {selectedMembers.length > 0 ? `${selectedMembers.length} selected` : 'Select all'}
                    </span>
                  </div>
                )}
                <div className="space-y-3">
                  {(isLoading || scraperRunLoading) ? (
                    <div className="text-center text-gray-500 py-8">
                      <div className="inline-flex items-center gap-2">
                        <RefreshCw className="w-4 h-4 animate-spin" />
                        Loading clinicians from SimplePractice...
                      </div>
                    </div>
                  ) : clinicianMembers.length === 0 ? (
                    <div className="text-center py-8">
                      {!hasConfiguredIntegration ? (
                        <div className="space-y-3">
                          <p className="text-gray-700 font-medium">No clinicians available yet.</p>
                          <p className="text-sm text-gray-500">Create Integration to start syncing clinicians from SimplePractice.</p>
                          <button
                            onClick={() => navigate('/practice/integration/simplepractice/form')}
                            className="inline-flex items-center px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors text-sm font-medium"
                          >
                            Create Integration
                          </button>
                        </div>
                      ) : (
                        <div className="space-y-2">
                          <p className="text-gray-700 font-medium">
                            Watching for new clinicians...
                          </p>
                          <p className="text-sm text-gray-500">
                            {isIntegrationConnected
                              ? 'Any newly available clinicians will show here automatically.'
                              : 'Integration is configured. New clinicians will show here once sync is active.'}
                          </p>
                        </div>
                      )}
                    </div>
                  ) : (
                    clinicianMembers.map((m) => {
                      const emailKey = (m.email || '').toLowerCase();
                      const nameParts = (m.name || '').trim().split(/\s+/);
                      const initials = getInitials(nameParts[0], nameParts.slice(1).join(' '));
                      const titles = (m.titles || []).join(', ');
                      const alreadyAdded = isAlreadyAdded(m);
                      const isSelected = alreadyAdded || !!selectedIds[emailKey];

                      return (
                        <motion.div
                          key={emailKey || m.id}
                          variants={rowVariants}
                          layout
                          whileHover={alreadyAdded ? undefined : { scale: 1.005, y: -1 }}
                          onClick={alreadyAdded ? undefined : () => toggleSelected(emailKey)}
                          className={`flex items-center justify-between p-4 backdrop-blur-sm bg-white/40 rounded-2xl shadow-[4px_4px_8px_rgba(0,0,0,0.04),-4px_-4px_8px_rgba(255,255,255,0.6)] hover:shadow-[6px_6px_12px_rgba(0,0,0,0.06),-6px_-6px_12px_rgba(255,255,255,0.8)] active:shadow-[inset_2px_2px_4px_rgba(0,0,0,0.03),inset_-2px_-2px_4px_rgba(255,255,255,0.7)] transition-all duration-200 border ${
                            alreadyAdded
                              ? 'opacity-60 border-white/30 cursor-default'
                              : `cursor-pointer ${isSelected ? 'border-indigo-300 ring-2 ring-indigo-200' : 'border-white/30'}`
                          }`}
                        >
                          {/* Left side: Checkbox, Avatar and Info */}
                          <div className="flex items-center gap-4">
                            <input
                              type="checkbox"
                              checked={isSelected}
                              disabled={alreadyAdded}
                              onChange={alreadyAdded ? undefined : () => toggleSelected(emailKey)}
                              onClick={(e) => e.stopPropagation()}
                              className="w-4 h-4 text-indigo-600 rounded border-gray-300 focus:ring-indigo-500 cursor-pointer disabled:cursor-not-allowed"
                            />
                            {/* Avatar */}
                            <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center text-purple-600 font-semibold shadow-[inset_2px_2px_4px_rgba(147,51,234,0.2),inset_-2px_-2px_4px_rgba(255,255,255,0.9)]">
                              {initials}
                            </div>
                            {/* Info */}
                            <div>
                              <h3 className="font-medium text-gray-900">{m.name}</h3>
                              <p className="text-sm text-gray-600">{m.email}</p>
                              {titles && (
                                <p className="text-sm text-gray-500">{titles}</p>
                              )}
                            </div>
                          </div>

                          {/* Right side: Added badge + Role */}
                          <div className="flex items-center gap-6">
                            {alreadyAdded && (
                              <div className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-700">
                                <CheckCircle className="w-4 h-4" />
                                Added
                              </div>
                            )}
                            <div className="text-right max-w-sm">
                              <p className="text-sm font-medium text-gray-900">{m.role}</p>
                              <p className="text-xs text-gray-500">Role</p>
                            </div>
                          </div>
                        </motion.div>
                      );
                    })
                  )}
                </div>
              </div>
            </motion.div>

            {/* Invited Clinicians - Payment Processing Section */}
            {invitedClinicians.length > 0 && (
              <motion.div
                initial={{ opacity: 0, y: 40 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
                className="backdrop-blur-sm bg-white/50 rounded-3xl shadow-[8px_8px_16px_rgba(0,0,0,0.06),-8px_-8px_16px_rgba(255,255,255,0.7)] overflow-hidden mt-6 border border-white/40"
              >
                <div className="p-6">
                  <div className="mb-4">
                    <h3 className="text-xl font-semibold text-gray-900 mb-1">Invited Clinicians</h3>
                    <p className="text-sm text-gray-500">Clinicians who have been successfully invited and are ready for payment processing.</p>
                  </div>

                  <div className="space-y-3">
                    {invitedClinicians.map((c: any) => {
                      const { percent } = getClinicianCompletion(c);
                      const initials = getInitials(c?.firstName || c?.given_name, c?.lastName || c?.family_name);
                      // Use subscriptionActivatedAt as sent date, fallback to other dates
                      const sentDate = formatDate(c?.subscriptionActivatedAt || c?.createdAt || c?.invite?.date_sent);
                      const lastActivity = formatDate(c?.updatedAt || c?.lastActive);

                      // Determine status using predefined statuses: new, invited, clicked, completed
                      const inviteStatus = getInviteStatus(c);
                      const statusBadge = getStatusBadge(inviteStatus);
                      const displayStatus = statusBadge;
                      const StatusIcon = displayStatus.icon || CheckSquare;
                      const title = c?.title || 'LMFT, MFCC';

                      return (
                        <motion.div
                          key={c._id || c.id}
                          variants={rowVariants}
                          layout
                          whileHover={{ scale: 1.005 }}
                          className="flex items-center justify-between p-4 border border-gray-200 rounded-lg hover:shadow-md transition-shadow bg-white"
                        >
                          <div className="flex items-center gap-4">
                            {/* Avatar */}
                            <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center text-purple-600 font-semibold">
                              {initials}
                            </div>
                            {/* Info */}
                            <div>
                              <h3 className="font-medium text-gray-900">
                                {c.fullName || `${c.firstName || ''} ${c.lastName || ''}`}
                              </h3>
                              <p className="text-sm text-gray-600">{c.email}</p>
                              <p className="text-sm text-gray-500">{title}</p>
                            </div>
                          </div>
                          <div className="flex items-center gap-6">
                            {/* Status */}
                            <div className="text-center">
                              {displayStatus && (
                                <div className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${displayStatus.className}`}>
                                  {StatusIcon && <StatusIcon className="w-4 h-4" />}
                                  <span className="capitalize">{displayStatus.text}</span>
                                </div>
                              )}
                              <p className="text-xs text-gray-500 mt-1">Sent: {sentDate}</p>
                            </div>
                            {/* Progress */}
                            <div className="text-center">
                              <div className="flex items-center gap-2 mb-1">
                                <div className="w-16 bg-gray-200 rounded-full h-2">
                                  <motion.div
                                    initial={{ width: 0 }}
                                    animate={{ width: `${percent}%` }}
                                    transition={{ duration: 0.6, delay: 0.1 }}
                                    className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                                  />
                                </div>
                                <span className="text-sm font-medium text-gray-900">{percent}%</span>
                              </div>
                              <p className="text-xs text-gray-500">Profile Complete</p>
                            </div>
                            {/* Last Activity */}
                            <div className="text-center">
                              <p className="text-sm font-medium text-gray-900">{lastActivity}</p>
                              <p className="text-xs text-gray-500">Last Activity</p>
                            </div>
                          </div>
                        </motion.div>
                      );
                    })}
                  </div>
                </div>
              </motion.div>
            )}
          </div>

          {/* Modals */}
          <AnimatePresence>
            {/* Send Invite Modal */}
            {pendingInviteData && (
              <SendInviteModal
                isOpen={isSendInviteModalOpen}
                onClose={() => {
                  setIsSendInviteModalOpen(false);
                  setPendingInviteData(null);
                  // Payment never happened; drop the persisted selection and billing snapshot
                  localStorage.removeItem('pendingScrapedClinicians');
                  localStorage.removeItem('pendingPurchaseBilling');
                }}
                billingData={pendingInviteData}
                userId={userId}
                practiceId={practiceId || pendingInviteData.practiceId || undefined}
                currentMonthlyCost={currentMonthlyCost}
              />
            )}

          </AnimatePresence>
        </div>
      </div>
    </DashboardLayout>
  );
}
